import { PLANS } from './plans';
import type { Feature, PlanTier } from './types';

// The PUBLIC face of the plan ladder: the names, prices and capability lines a
// human reads on the pricing page, in the account app, and in an upgrade prompt.
//
// `plans.ts` decides what a plan can DO; this file decides what we SAY it can
// do. They are two different failure modes — a gate that is too generous leaks
// revenue, a marketing claim that is too generous is a lie — so they are kept in
// separate files and reconciled by `catalog.test.ts`, which fails the build if
// any capability here names a `Feature` the corresponding plan does not grant.
//
// Prices live in the Paddle catalog; the strings here are display copy only and
// never drive a charge. Keep them in step with the Paddle price IDs.

/** One user-visible line on a plan's capability list. */
export interface PlanCapability {
  label: string;
  /**
   * The gate that enforces it, or `null` for capabilities with no gate at all —
   * the bundled Studio GUI (editor, environments, mocks, plans, history,
   * import, GitHub sync) is ungated core and names no `Feature`.
   */
  feature: Feature | null;
}

export interface PlanPrice {
  /** Headline price when billed monthly, e.g. `'$18.99'`. */
  monthly: string;
  /** Monthly-equivalent when billed annually, e.g. `'$17.09'`. */
  annualMonthly: string;
  /** Total charged once a year, e.g. `'$205.09'`. */
  annualTotal: string;
  /** Whether every figure above is per seat (Team) rather than per account. */
  perSeat: boolean;
}

export interface PublicPlan {
  tier: PlanTier;
  /** Display name, e.g. `'Pro'`. */
  name: string;
  /** One-line positioning statement. */
  blurb: string;
  /** `null` for Free — there is nothing to charge. */
  price: PlanPrice | null;
  workspaces: number | 'unlimited';
  capabilities: PlanCapability[];
  /** Whether the plan appears on the public pricing grid. */
  listed: boolean;
}

/** Discount applied to annual billing. Copy says "Save 10%" — NOT "2 months
 *  free", which would overstate it (10% is ~1.2 months). */
export const ANNUAL_DISCOUNT_PERCENT = 10;

/** The four Studio-GUI capability lines every tier carries. Ungated core. */
const STUDIO_CORE: PlanCapability[] = [
  { label: 'Request editor, environments and run history', feature: null },
  { label: 'Local mock servers', feature: null },
  { label: 'Execution plans — author and run them in the app', feature: null },
  { label: 'OpenAPI and collection import, GitHub-backed sync', feature: null },
];

/** Ordered cheapest-first. `cheapestPlanFor` depends on that ordering. */
export const PLAN_CATALOG: PublicPlan[] = [
  {
    tier: 'free',
    name: 'Free',
    blurb: 'The full API workspace, no account needed.',
    price: null,
    workspaces: 1,
    listed: true,
    capabilities: [{ label: '1 workspace', feature: 'workspaces' }, ...STUDIO_CORE],
  },
  {
    tier: 'basic',
    name: 'Basic',
    blurb: 'See what your code actually implements, and what drifted.',
    price: {
      monthly: '$18.99',
      annualMonthly: '$17.09',
      annualTotal: '$205.09',
      perSeat: false,
    },
    workspaces: 3,
    listed: true,
    capabilities: [
      { label: '3 workspaces', feature: 'workspaces' },
      { label: 'Everything in Free', feature: null },
      { label: 'Index map — every endpoint, mapped to its code', feature: 'code-graph' },
      { label: 'Review — pull-request and spec-drift reports', feature: 'review' },
      { label: 'Drift-infill scaffolding', feature: 'byoc' },
    ],
  },
  {
    tier: 'pro',
    name: 'Pro',
    blurb: 'Put your AI tools on the same map you are working from.',
    price: {
      monthly: '$27.99',
      annualMonthly: '$25.19',
      annualTotal: '$302.29',
      perSeat: false,
    },
    workspaces: 10,
    listed: true,
    capabilities: [
      { label: '10 workspaces', feature: 'workspaces' },
      { label: 'Everything in Basic', feature: null },
      { label: 'AI Assistant — bring your own key', feature: 'assistant' },
      { label: 'MCP server for Claude, Cursor, Copilot and more', feature: 'mcp' },
      { label: 'Endpoint context graph', feature: 'context-graph' },
      { label: 'GitLab, Bitbucket and Azure DevOps', feature: 'git-providers' },
    ],
  },
  {
    tier: 'team',
    name: 'Team',
    blurb: 'Make contract drift a build failure, for the whole team.',
    price: {
      monthly: '$36.99',
      annualMonthly: '$33.29',
      annualTotal: '$399.49',
      perSeat: true,
    },
    workspaces: 'unlimited',
    listed: true,
    capabilities: [
      { label: 'Unlimited workspaces', feature: 'workspaces' },
      { label: 'Everything in Pro', feature: null },
      { label: 'Command-line interface for CI pipelines', feature: 'cli' },
      { label: 'Seats and per-device activation', feature: null },
      { label: 'Single sign-on (OIDC)', feature: 'sso' },
    ],
  },
  {
    tier: 'enterprise',
    name: 'Enterprise',
    blurb: 'Org-managed licensing, offline activation and white-label builds.',
    price: null,
    workspaces: 'unlimited',
    listed: false,
    capabilities: [
      { label: 'Everything in Team', feature: null },
      { label: 'Offline and self-signed licences', feature: null },
      { label: 'Single sign-on (OIDC)', feature: 'sso' },
    ],
  },
];

/** The public plan for `tier`, or `undefined` if it is not in the catalog. */
export function publicPlan(tier: PlanTier): PublicPlan | undefined {
  return PLAN_CATALOG.find((p) => p.tier === tier);
}

/** Every plan shown on the pricing grid, cheapest first. */
export function listedPlans(): PublicPlan[] {
  return PLAN_CATALOG.filter((p) => p.listed);
}

/**
 * The cheapest LISTED plan that grants `feature`, or `null` if none does.
 *
 * Resolved against `PLANS` — the gate — rather than the capability lists here,
 * so an upgrade prompt can never name a plan that would still deny the user.
 * This is what replaced the hard-coded "upgrade to Pro" copy.
 */
export function cheapestPlanFor(feature: Feature): PublicPlan | null {
  return PLAN_CATALOG.find((p) => p.listed && PLANS[p.tier].features.includes(feature)) ?? null;
}
